/**
 * Der zusammengesetzte Bestand und die Abfragen darauf.
 *
 * **Warum hier Abfragen stehen und nicht nur Daten.** Das Dashboard aus
 * `07` §7.4 stellt sieben Fragen, und jede davon ist eine Auswertung über
 * mehrere Objektarten — „wo fehlt eine Rückmeldung" verbindet Einsatz, Monteur
 * und Zeit. Würde jede Oberfläche das selbst rechnen, gäbe es die Regel
 * mehrfach: einmal im Portal, einmal in der Monteur-App, und beim ersten
 * Widerspruch weiß niemand, welche stimmt.
 *
 * Die Funktionen sind deshalb das, was später der Lesepfad über die
 * Projektionen wird (`services/projections`, ADR-021). Sie hier zu haben ist
 * der eigentliche Wert des Demostands: Wenn der echte Adapter kommt, wird
 * gegen dieselben Signaturen getauscht.
 *
 * Import-Grenze nach ADR-016: kein React, kein HTTP, keine Node-API.
 */

import type {
  Ablageobjekt,
  Auftrag,
  Auftragszeit,
  Bestand,
  Einsatz,
  Eingangsvorgang,
  Kante,
  Kunde,
  MagicLink,
  Nachricht,
  Person,
  Servicebericht,
} from "./modell.js";
import type { ResourceRef } from "@hwc/contracts";
import { formatResourceRef } from "@hwc/contracts";

import { DEMO_TENANT } from "./modell.js";
import { BETRIEB, PERSONEN, kunden } from "./stammdaten.js";
import { auftraege, einsaetze, material, zeiten } from "./auftraege.js";
import { eingang, rechnungen } from "./eingang.js";
import { ablage } from "./ablage.js";
import { chatbeitraege, chatraeume, nachrichten, termine } from "./kommunikation.js";
import { audit, berichte, kanten, magiclinks, wartungsplaene } from "./abschluss.js";

/**
 * Baut den vollständigen Bestand zu einem Bezugstag.
 *
 * Der Bezugstag ist ein Parameter und kein `new Date()` im Rumpf: Ein Test,
 * der den heutigen Tag nicht festhalten kann, ist an einem Montag grün und am
 * Samstag rot, ohne dass sich etwas geändert hätte.
 */
export function erzeugeBestand(bezugstag: Date = new Date()): Bestand {
  return {
    tenant: DEMO_TENANT,
    betrieb: BETRIEB,
    personen: PERSONEN,
    kunden: kunden(bezugstag),
    auftraege: auftraege(bezugstag),
    einsaetze: einsaetze(bezugstag),
    zeiten: zeiten(bezugstag),
    material: material(bezugstag),
    eingang: eingang(bezugstag),
    rechnungen: rechnungen(bezugstag),
    ablage: ablage(bezugstag),
    nachrichten: nachrichten(bezugstag),
    chatraeume: chatraeume(),
    chatbeitraege: chatbeitraege(bezugstag),
    termine: termine(bezugstag),
    berichte: berichte(bezugstag),
    wartungsplaene: wartungsplaene(bezugstag),
    magiclinks: magiclinks(bezugstag),
    kanten: kanten(bezugstag),
    audit: audit(bezugstag),
  };
}

// ───────────────────────── Nachschlagen ─────────────────────────

/** Vergleicht zwei Refs. Über die Zeichenkettenform, weil Objekte nicht gleich sind. */
export function gleich(a: ResourceRef, b: ResourceRef): boolean {
  return formatResourceRef(a) === formatResourceRef(b);
}

export function findePerson(b: Bestand, r: ResourceRef | undefined): Person | undefined {
  return r ? b.personen.find((p) => gleich(p.ref, r)) : undefined;
}

export function findeKunde(b: Bestand, r: ResourceRef | undefined): Kunde | undefined {
  return r ? b.kunden.find((k) => gleich(k.ref, r)) : undefined;
}

export function findeAuftrag(b: Bestand, r: ResourceRef | undefined): Auftrag | undefined {
  return r ? b.auftraege.find((a) => gleich(a.ref, r)) : undefined;
}

export function findeEinsatz(b: Bestand, r: ResourceRef | undefined): Einsatz | undefined {
  return r ? b.einsaetze.find((e) => gleich(e.ref, r)) : undefined;
}

export function findeAblage(b: Bestand, r: ResourceRef | undefined): Ablageobjekt | undefined {
  return r ? b.ablage.find((o) => gleich(o.ref, r)) : undefined;
}

/** Der Auftrag zu einer Auftragsnummer — das, was am Telefon genannt wird. */
export function auftragNachNummer(b: Bestand, nummer: string): Auftrag | undefined {
  return b.auftraege.find((a) => a.nummer === nummer);
}

/** Der Kunde zu einer Kundennummer. */
export function kundeNachNummer(b: Bestand, nummer: string): Kunde | undefined {
  return b.kunden.find((k) => k.nummer === nummer);
}

// ───────────────────────── Zeitfenster ─────────────────────────

/** Der Kalendertag einer ISO-Zeitangabe, in der Zone, die darin steht. */
export function tagVon(isoZeit: string): string {
  return isoZeit.slice(0, 10);
}

function heuteAls(bezugstag: Date): string {
  const z = (n: number) => (n < 10 ? `0${n}` : String(n));
  return `${bezugstag.getFullYear()}-${z(bezugstag.getMonth() + 1)}-${z(bezugstag.getDate())}`;
}

/**
 * Die Einsätze eines Tages.
 *
 * Sortiert nach Beginn, nicht nach Anlagezeit: Eine Tagesliste, die nicht der
 * Uhr folgt, ist für einen Monteur wertlos — er arbeitet sie von oben nach
 * unten ab.
 */
export function einsaetzeAmTag(
  b: Bestand,
  datum: string,
  monteur?: ResourceRef,
): readonly Einsatz[] {
  return b.einsaetze
    .filter((e) => tagVon(e.von) === datum)
    .filter((e) => !monteur || (e.monteur !== undefined && gleich(e.monteur, monteur)))
    .slice()
    .sort((x, y) => x.von.localeCompare(y.von));
}

export function einsaetzeHeute(
  b: Bestand,
  bezugstag: Date,
  monteur?: ResourceRef,
): readonly Einsatz[] {
  return einsaetzeAmTag(b, heuteAls(bezugstag), monteur);
}

/**
 * Die kommenden Einsätze ab morgen, aufsteigend.
 *
 * `07` §7.7 gliedert die Monteursicht in „heute, morgen und später" —
 * abgeschlossene Einsätze erscheinen „nur bei Bedarf". Deshalb filtert diese
 * Funktion Endzustände heraus: Was durch ist, gehört nicht in die Vorschau.
 */
export function kommendeEinsaetze(
  b: Bestand,
  bezugstag: Date,
  monteur?: ResourceRef,
): readonly Einsatz[] {
  const heute = heuteAls(bezugstag);
  return b.einsaetze
    .filter((e) => tagVon(e.von) > heute)
    .filter((e) => e.zustand !== "geprueft" && e.zustand !== "storniert")
    .filter((e) => !monteur || (e.monteur !== undefined && gleich(e.monteur, monteur)))
    .slice()
    .sort((x, y) => x.von.localeCompare(y.von));
}

// ───────────────────────── Auftragsakte ─────────────────────────

/**
 * Alles, was über den Link-Graphen an einem Auftrag hängt.
 *
 * Das ist `07` §7.1 in einer Funktion: Unter „Auftrag 2481" erscheinen
 * Aufgaben, Dateien, Dokumente, Mails, Chatraum und Termin gemeinsam. Die
 * Kanten werden in **beide** Richtungen gelesen — eine Datei zeigt mit
 * `belongs_to_job` auf den Auftrag, der Auftrag zeigt mit
 * `belongs_to_customer` auf den Kunden. Wer nur eine Richtung liest, verliert
 * die Hälfte der Akte.
 */
export function verknuepftMit(b: Bestand, objekt: ResourceRef): readonly Kante[] {
  return b.kanten.filter(
    (k) => (gleich(k.source, objekt) || gleich(k.target, objekt)) && !k.deletedAt,
  );
}

/** Die Refs, die von einem Objekt aus über die Kanten erreichbar sind. */
export function nachbarn(b: Bestand, objekt: ResourceRef): readonly ResourceRef[] {
  const treffer: ResourceRef[] = [];
  for (const k of verknuepftMit(b, objekt)) {
    treffer.push(gleich(k.source, objekt) ? k.target : k.source);
  }
  return treffer;
}

export function einsaetzeZuAuftrag(b: Bestand, auftrag: ResourceRef): readonly Einsatz[] {
  return b.einsaetze
    .filter((e) => gleich(e.auftrag, auftrag))
    .slice()
    .sort((x, y) => x.von.localeCompare(y.von));
}

export function zeitenZuEinsatz(b: Bestand, einsatz: ResourceRef): readonly Auftragszeit[] {
  return b.zeiten.filter((z) => gleich(z.einsatz, einsatz));
}

export function berichteZuAuftrag(b: Bestand, auftrag: ResourceRef): readonly Servicebericht[] {
  return b.berichte
    .filter((s) => gleich(s.auftrag, auftrag))
    .slice()
    .sort((x, y) => y.version - x.version);
}

/**
 * Die Dauer einer Zeitbuchung in Minuten.
 *
 * Über `Date.parse`, weil die Zeichenketten ihren Zonenversatz mitbringen und
 * ein Einsatz über die Zeitumstellung sonst um eine Stunde danebenläge.
 */
export function dauerMinuten(von: string, bis: string): number {
  return Math.max(0, Math.round((Date.parse(bis) - Date.parse(von)) / 60000));
}

/** Summe der gebuchten Minuten, wahlweise nur die abrechenbaren. */
export function summeMinuten(
  zeitliste: readonly Auftragszeit[],
  nurAbrechenbar = false,
): number {
  return zeitliste
    .filter((z) => !nurAbrechenbar || z.abrechenbar)
    .reduce((summe, z) => summe + dauerMinuten(z.von, z.bis), 0);
}

// ───────────────────────── Dashboardfragen ─────────────────────────

/**
 * „Welche Monteure sind verfügbar oder verspätet?" — `07` §7.4.
 *
 * Verspätet heißt hier: Das Zeitfenster hat begonnen, der Einsatz steht aber
 * noch auf `geplant`. Das ist bewusst keine GPS-Auswertung — `07` §7.15
 * schließt heimliches Tracking ausdrücklich aus. Was die Plattform weiß, ist
 * was jemand gemeldet hat, und mehr soll sie nicht wissen.
 */
export function verspaeteteEinsaetze(b: Bestand, jetzt: Date): readonly Einsatz[] {
  const marke = jetzt.getTime();
  return b.einsaetze.filter(
    (e) => e.zustand === "geplant" && Date.parse(e.von) < marke && e.monteur !== undefined,
  );
}

/** „Wo fehlt eine Rückmeldung?" — Einsätze, die warten oder zurückkamen. */
export function wartetAufRueckmeldung(b: Bestand): readonly Einsatz[] {
  return b.einsaetze.filter((e) => e.zustand === "wartet" || e.zustand === "abschluss");
}

/** „Welche Vorgänge benötigen menschliche Prüfung?" */
export function offenerEingang(b: Bestand): readonly Eingangsvorgang[] {
  return b.eingang
    .filter((v) => v.status === "neu" || v.status === "in_pruefung" || v.status === "quarantaene")
    .slice()
    .sort((x, y) => y.eingegangen.localeCompare(x.eingegangen));
}

/** „Welche neuen E-Mails sind keinem Auftrag zugeordnet?" */
export function ungeleseneNachrichten(b: Bestand): readonly Nachricht[] {
  return b.nachrichten.filter((n) => !n.gelesen && n.richtung === "eingehend");
}

/**
 * „Welche MagicLinks laufen bald ab?"
 *
 * Widerrufene fallen heraus: Ein widerrufener Link läuft nicht ab, er ist tot.
 * Ihn in einer Ablaufwarnung zu führen würde zum Verlängern einladen.
 */
export function baldAblaufendeLinks(
  b: Bestand,
  jetzt: Date,
  innerhalbTagen = 3,
): readonly MagicLink[] {
  const grenze = jetzt.getTime() + innerhalbTagen * 86_400_000;
  return b.magiclinks.filter(
    (m) => !m.widerrufen && Date.parse(m.laeuftAb) <= grenze && Date.parse(m.laeuftAb) > jetzt.getTime(),
  );
}

/** Serviceberichte, die auf eine Entscheidung des Büros warten. */
export function berichteZurPruefung(b: Bestand): readonly Servicebericht[] {
  return b.berichte.filter((s) => s.zustand === "zur_pruefung");
}

/** Wartungspläne, deren Fälligkeit in den Vorlauf gerutscht ist. */
export function faelligeWartung(b: Bestand, jetzt: Date) {
  return b.wartungsplaene.filter((p) => {
    if (p.status !== "aktiv") return false;
    const faellig = Date.parse(`${p.naechsteFaelligkeit}T00:00:00Z`);
    return faellig - jetzt.getTime() <= p.vorlaufTage * 86_400_000;
  });
}

// ───────────────────────── Suche ─────────────────────────

/** Ein Suchtreffer über alle Objektarten — die Grundlage für `PORTAL-003`. */
export interface Treffer {
  readonly ref: ResourceRef;
  readonly art: string;
  readonly titel: string;
  readonly zusatz?: string;
}

/**
 * Einheitliche Suche ohne KI — `07` §7.15 führt sie im MVP, ADR-017 hält die
 * KI vollständig dahinter.
 *
 * Bewusst simpel: Teilzeichenkette, ohne Gewichtung, ohne Stammformen. Das ist
 * keine Suchmaschine, sondern die Antwort auf die Frage, ob die
 * **Ergebnisdarstellung** über alle Quellen hinweg brauchbar ist — und die
 * lässt sich mit einem `includes` genauso beurteilen wie mit einem Index.
 * `PORTAL-003` ersetzt den Rumpf, nicht die Anzeige.
 */
export function suche(b: Bestand, frage: string): readonly Treffer[] {
  const q = frage.trim().toLowerCase();
  if (q.length < 2) return [];

  const passt = (...felder: readonly (string | undefined)[]): boolean =>
    felder.some((f) => f !== undefined && f.toLowerCase().includes(q));

  const treffer: Treffer[] = [];

  for (const k of b.kunden) {
    if (passt(k.name, k.nummer, k.anschrift.ort, k.anschrift.strasse, k.externeKundennummer)) {
      treffer.push({ ref: k.ref, art: "Kunde", titel: k.name, zusatz: k.nummer });
    }
  }
  for (const a of b.auftraege) {
    if (passt(a.titel, a.nummer, a.beschreibung, a.gewerk)) {
      treffer.push({ ref: a.ref, art: "Auftrag", titel: a.titel, zusatz: `Auftrag ${a.nummer}` });
    }
  }
  for (const e of b.einsaetze) {
    if (passt(e.titel, e.hinweis)) {
      treffer.push({ ref: e.ref, art: "Einsatz", titel: e.titel, zusatz: e.zustand });
    }
  }
  for (const o of b.ablage) {
    if (passt(o.name, o.inhalt, ...(o.schlagworte ?? []))) {
      treffer.push({
        ref: o.ref,
        art: o.art === "wissen" ? "Wissen" : o.art === "dokument" ? "Dokument" : "Datei",
        titel: o.name,
        ...(o.pfad ? { zusatz: o.pfad } : {}),
      });
    }
  }
  for (const n of b.nachrichten) {
    if (passt(n.betreff, n.ausschnitt, n.von)) {
      treffer.push({ ref: n.ref, art: "E-Mail", titel: n.betreff, zusatz: n.von });
    }
  }
  for (const v of b.eingang) {
    if (passt(v.betreff, v.absender)) {
      treffer.push({ ref: v.ref, art: "Eingang", titel: v.betreff, zusatz: v.status });
    }
  }

  return treffer;
}
